// Money → Overview (spec 044 D100–D112, plan slice 9): the four stat tiles, the revenue chart,
// Sessions in and out, Recent transactions and Upcoming payouts — Figma "Financial · Overview"
// (11355:26773). Each card below the tiles is a section with an h2; `state` is "loading",
// "failed", "empty" (the chart only — buildReportStats has not run) or "ready".
import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  RiArrowRightDownLine,
  RiArrowRightLine,
  RiArrowRightUpLine,
  RiHandCoinLine,
  RiSendPlane2Line,
  RiShoppingBag3Line,
  RiTimerLine,
  RiWallet3Line,
} from "@remixicon/react";
import { ChartTooltip } from "../../components/kit/ChartTooltip";
import { ErrorState } from "../../components/kit/States";
import StatusChip from "../../components/kit/StatusChip";
import { srOnly, tokens } from "../../utils/figmaTokens";
import { money } from "../../utils/queuePresentation";
import { WHAT_LOOK } from "../Transactions/whatLook";

const cardSx = {
  bgcolor: tokens.surface.default,
  border: `1px solid ${tokens.border.default}`,
  borderRadius: "16px",
  minWidth: 0,
};
const titleSx = { fontSize: 18, lineHeight: "30px", fontWeight: 500, color: tokens.text.primary };
const outline = { "&:focus-visible": { outline: `2px solid ${tokens.brand.border}`, outlineOffset: "2px" } };

const PILL_TONES = {
  up: tokens.success,
  success: tokens.success,
  down: tokens.error,
  flat: tokens.neutral,
  warning: tokens.warning,
  info: tokens.info,
  brand: { bg: tokens.brand.subtle, ink: tokens.brand.text },
};

export const Pill = ({ tone, text, before, Icon }) => {
  const c = PILL_TONES[tone] || PILL_TONES.flat;
  return (
    <Box
      component="span"
      title={before ? `Compared with ${before}` : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        px: Icon ? "8px" : "11px",
        py: "4px",
        borderRadius: "999px",
        bgcolor: c.bg,
        color: c.ink,
      }}
    >
      {Icon && <Icon size={13} aria-hidden />}
      <Typography component="span" variant="labelMedium" sx={{ color: "inherit", fontWeight: 600, whiteSpace: "nowrap" }}>
        {text}
      </Typography>
      {before && <Box component="span" sx={srOnly}>{` compared with ${before}`}</Box>}
    </Box>
  );
};

export const StatTile = ({ Icon, tone, label, value, pill, loading }) => (
  <Box sx={{ ...cardSx, p: { xs: "16px", sm: "18px 24px" }, display: "flex", flexDirection: "column", gap: "14px" }}>
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          bgcolor: tone.bg,
          color: tone.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} />
      </Box>
      <Box sx={{ flex: 1 }} />
      {!loading && pill && <Pill {...pill} />}
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography variant="titleMetric" sx={{ color: tokens.text.primary }}>
        {loading ? <Skeleton width={96} /> : value}
      </Typography>
      <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const anchorFor = (i) => (i < 2 ? "start" : i > 9 ? "end" : "center");
// A bar's height in the 190 px chart, its value against the largest of the twelve.
const barHeight = (value, largest) => (largest === 0 ? 4 : Math.max(4, Math.round((value / largest) * 190)));

export const RevenueCard = ({ state, bars, line, current, onRetry }) => {
  const [hover, setHover] = React.useState(null);
  const largest = Math.max(0, ...(bars || []).map((b) => b.value));
  const LineIcon = !line ? null : line.tone === "up" ? RiArrowRightUpLine : line.tone === "down" ? RiArrowRightDownLine : RiArrowRightLine;
  const lineColor = !line ? null : line.tone === "up" ? tokens.success.main : line.tone === "down" ? tokens.error.main : tokens.text.tertiary;
  return (
    <Box component="section" aria-labelledby="overview-revenue-title" sx={{ ...cardSx, pt: "20px", pb: "22px", px: { xs: "16px", sm: "24px" }, display: "flex", flexDirection: "column", gap: "18px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Typography id="overview-revenue-title" component="h2" sx={titleSx}>
          Revenue · last 12 months
        </Typography>
        <Pill tone="brand" text="Gross" />
      </Box>
      {state === "loading" ? (
        <Skeleton variant="rounded" height={216} />
      ) : state === "failed" ? (
        <ErrorState compact title="The chart could not load" message="Try again in a moment." onRetry={onRetry} />
      ) : state === "empty" ? (
        <Typography variant="bodyMedium" sx={{ color: tokens.text.secondary, py: "24px" }}>
          The chart fills in within the hour.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <Box sx={{ position: "relative" }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", height: 190, gap: { xs: "4px", sm: "10px" } }}>
              {bars.map((bar, i) => (
                <Box
                  key={bar.key}
                  tabIndex={0}
                  role="img"
                  aria-label={`${bar.full}: £${money(bar.value)}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onBlur={() => setHover(null)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: barHeight(bar.value, largest),
                    borderRadius: "8px",
                    bgcolor: bar.chosen ? tokens.brand.primary : tokens.brand.subtle,
                    cursor: "default",
                    ...outline,
                  }}
                />
              ))}
            </Box>
            {hover != null && (
              <ChartTooltip
                x={`${((hover + 0.5) / 12) * 100}%`}
                y={190 - barHeight(bars[hover].value, largest)}
                below={190 - barHeight(bars[hover].value, largest) < 100}
                anchor={anchorFor(hover)}
                title={bars[hover].full}
                value={`£${money(bars[hover].value)}`}
                note={bars[hover].chosen && current ? "Paid top-ups so far" : "Paid top-ups"}
              />
            )}
          </Box>
          <Box aria-hidden sx={{ display: "flex", gap: { xs: "4px", sm: "10px" } }}>
            {bars.map((bar) => (
              <Typography key={bar.key} variant="labelMedium" sx={{ flex: 1, textAlign: "center", color: tokens.text.tertiary }}>
                {bar.short}
              </Typography>
            ))}
          </Box>
          {line && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <LineIcon size={16} aria-hidden color={lineColor} />
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                {line.text}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const FILL_COLOR = { brand: tokens.brand.primary, info: tokens.info.main, warning: tokens.warning.main, neutral: tokens.icon.secondary, success: tokens.success.main };

export const MovesCard = ({ rows, loading }) => (
  <Box component="section" aria-labelledby="overview-moves-title" sx={{ ...cardSx, width: { lg: 400 }, pt: "20px", pb: "22px", px: { xs: "16px", sm: "24px" }, display: "flex", flexDirection: "column", gap: "14px" }}>
    <Typography id="overview-moves-title" component="h2" sx={titleSx}>
      Sessions in and out
    </Typography>
    {rows.map((r) => (
      <Box key={r.key} sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
          <Typography variant="bodySmall" sx={{ flex: 1, color: tokens.text.secondary }}>
            {r.label}
          </Typography>
          <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
            {loading ? <Skeleton width={72} /> : r.value}
          </Typography>
        </Box>
        {r.note && (
          <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
            {r.note}
          </Typography>
        )}
        <Box sx={{ height: 6, borderRadius: "999px", bgcolor: tokens.surface.muted }}>
          <Box sx={{ width: `${Math.round(r.fill * 100)}%`, height: "100%", borderRadius: "999px", bgcolor: FILL_COLOR[r.tone] }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const insetOutline = { "&:focus-visible": { outline: `2px solid ${tokens.brand.border}`, outlineOffset: "-2px" } };

export const RecentCard = ({ state, views, monthName, canOpen, onRetry }) => (
  <Box component="section" aria-labelledby="overview-recent-title" sx={{ ...cardSx, overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        pl: { xs: "16px", sm: "24px" },
        pr: { xs: "16px", sm: "20px" },
        py: "18px",
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
    >
      <Typography id="overview-recent-title" component="h2" sx={{ ...titleSx, flex: 1 }}>
        Recent transactions
      </Typography>
      <Box
        component={RouterLink}
        to="/transactions"
        sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: 14, fontWeight: 600, color: tokens.text.link, textDecoration: "none", ...outline }}
      >
        View all
        <RiArrowRightLine size={16} aria-hidden />
      </Box>
    </Box>
    {state === "loading" ? (
      Array.from({ length: 5 }, (_, i) => <Skeleton key={i} variant="rectangular" height={60} sx={{ mx: "24px", my: "6px", borderRadius: "10px" }} />)
    ) : state === "failed" ? (
      <ErrorState compact title="The transactions could not load" onRetry={onRetry} />
    ) : views.length === 0 ? (
      <Typography variant="bodyMedium" sx={{ color: tokens.text.secondary, px: "24px", py: "24px" }}>
        No transactions in {monthName}.
      </Typography>
    ) : (
      views.map((view, i) => {
        const look = WHAT_LOOK[view.look] || WHAT_LOOK.other;
        const row = (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              px: { xs: "16px", sm: "24px" },
              py: "12px",
              borderBottom: i < views.length - 1 ? `1px solid ${tokens.border.subtle}` : "none",
            }}
          >
            <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", bgcolor: look.tone.bg, color: look.tone.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <look.Icon size={18} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                {view.person.name}
              </Typography>
              <Typography variant="labelMedium" noWrap sx={{ display: "block", color: tokens.text.tertiary }}>
                {view.title}
              </Typography>
            </Box>
            <Typography variant="labelMedium" noWrap sx={{ display: { xs: "none", sm: "block" }, width: 120, textAlign: "right", color: tokens.text.tertiary }}>
              {view.date}
            </Typography>
            <Typography variant="bodySmallStrong" noWrap sx={{ width: 90, textAlign: "right", color: view.negative ? tokens.error.ink : tokens.text.primary }}>
              {view.amount}
            </Typography>
            <Box sx={{ width: 110, display: { xs: "none", sm: "flex" }, justifyContent: "flex-end" }}>
              <StatusChip label={view.pill} color={view.tone} />
            </Box>
          </Box>
        );
        return canOpen && view.personId ? (
          <Box
            key={view.id}
            component={RouterLink}
            to={`/students/${view.personId}`}
            sx={{ textDecoration: "none", "&:hover": { bgcolor: tokens.hover }, ...insetOutline }}
          >
            {row}
          </Box>
        ) : (
          <Box key={view.id}>{row}</Box>
        );
      })
    )}
  </Box>
);

export const PayoutsCard = ({ state, rows, cycleLabel, canOpen, onRetry }) => (
  <Box component="section" aria-labelledby="overview-payouts-title" sx={{ ...cardSx, width: { lg: 400 }, pt: "20px", pb: "22px", px: { xs: "16px", sm: "24px" }, display: "flex", flexDirection: "column", gap: "14px" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <Typography id="overview-payouts-title" component="h2" sx={titleSx}>
        Upcoming payouts
      </Typography>
      {cycleLabel && <Pill tone="warning" Icon={RiTimerLine} text={cycleLabel} />}
    </Box>
    {state === "loading" ? (
      Array.from({ length: 3 }, (_, i) => <Skeleton key={i} variant="rectangular" height={44} sx={{ borderRadius: "8px" }} />)
    ) : state === "failed" ? (
      <ErrorState compact title="Upcoming payouts could not load" onRetry={onRetry} />
    ) : rows.length === 0 ? (
      <Typography variant="bodyMedium" sx={{ color: tokens.text.secondary }}>
        Nobody is owed anything right now.
      </Typography>
    ) : (
      rows.map((r) => {
        const inner = (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                {r.name}
              </Typography>
              <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
                {r.hours}
              </Typography>
            </Box>
            <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
              {r.owed}
            </Typography>
          </Box>
        );
        return (
          <Box key={r.id} sx={{ pb: "14px", borderBottom: `1px solid ${tokens.border.subtle}`, "&:last-of-type": { borderBottom: "none", pb: 0 } }}>
            {canOpen ? (
              <Box component={RouterLink} to={`/tutors/${r.id}`} sx={{ textDecoration: "none", display: "block", ...outline }}>
                {inner}
              </Box>
            ) : (
              inner
            )}
          </Box>
        );
      })
    )}
    <Box
      component={RouterLink}
      to="/payouts"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        width: "100%",
        bgcolor: tokens.brand.primary,
        borderRadius: "10px",
        py: "11px",
        textDecoration: "none",
        "&:hover": { bgcolor: tokens.brand.hover },
        ...outline,
      }}
    >
      <RiSendPlane2Line size={18} color={tokens.brand.on} />
      <Typography sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, letterSpacing: "0.1px", color: tokens.brand.on }}>
        Open Payouts
      </Typography>
    </Box>
  </Box>
);

export const STAT_TILES = [
  { key: "gross", Icon: RiWallet3Line, tone: { bg: tokens.brand.subtle, main: tokens.icon.brand } },
  { key: "sold", Icon: RiShoppingBag3Line, tone: tokens.success },
  { key: "owed", Icon: RiSendPlane2Line, tone: tokens.warning },
  { key: "paid", Icon: RiHandCoinLine, tone: tokens.info },
];
